import type { PlotPurchaseRequest, PlotPurchaseResponse, RealEstateListing, RealEstateListingsResponse, WalletsResponse } from "@townsy/contracts";
import { isApiError } from "../api/client";
import { formatCoins } from "../lib/wallet";

/**
 * Pure purchase logic for the Real Estate office (ADR-0004 #6, docs/04 sections 6 and 9).
 * The browser never decides whether a purchase happens: it only explains why the Buy button is
 * off, sends one idempotent request per confirmation, and maps the server's error codes to text.
 */

export type BuyDisabledCode =
  | "SIGN_IN_REQUIRED"
  | "EMAIL_NOT_VERIFIED"
  | "PLOT_LIMIT_REACHED"
  | "FEATURE_DISABLED"
  | "NOT_SOLD_FOR_COINS"
  | "WALLET_LOADING"
  | "WALLET_UNAVAILABLE"
  | "INSUFFICIENT_COINS";

export interface BuyAvailability {
  enabled: boolean;
  code: BuyDisabledCode | null;
  /** Exact explanation shown next to the disabled button (null when enabled). */
  reason: string | null;
  /** Coins still missing when the balance is too low (0 otherwise). */
  shortfall: number;
}

export interface BuyInputs {
  listings: Pick<RealEstateListingsResponse, "blockedReason" | "plotsPerAccount" | "ownedPropertyIds">;
  listing: Pick<RealEstateListing, "offer">;
  /** Null while loading / for guests. */
  wallets: Pick<WalletsResponse, "coins"> | null;
  walletState: "loading" | "ready" | "error" | "none";
}

const ok: BuyAvailability = { enabled: false, code: null, reason: null, shortfall: 0 };
const off = (code: BuyDisabledCode, reason: string, shortfall = 0): BuyAvailability => ({ ...ok, code, reason, shortfall });

export function buyAvailability({ listings, listing, wallets, walletState }: BuyInputs): BuyAvailability {
  switch (listings.blockedReason) {
    case "SIGN_IN_REQUIRED":
      return off("SIGN_IN_REQUIRED", "Sign in to buy a plot. Guests can look around but cannot own homes.");
    case "EMAIL_NOT_VERIFIED":
      return off("EMAIL_NOT_VERIFIED", "Verify your email address to buy a plot.");
    case "PLOT_LIMIT_REACHED":
      return off(
        "PLOT_LIMIT_REACHED",
        listings.plotsPerAccount === 1
          ? "One plot per account: you already own a plot."
          : `You already own ${listings.ownedPropertyIds.length} plots (limit ${listings.plotsPerAccount} per account).`,
      );
    case "FEATURE_DISABLED":
      return off("FEATURE_DISABLED", "Plot sales are switched off in this environment.");
    default:
      break;
  }
  const price = listing.offer.coins;
  if (price === null) return off("NOT_SOLD_FOR_COINS", "This plot is not offered for coins.");
  if (walletState === "loading" || (walletState === "ready" && !wallets)) return off("WALLET_LOADING", "Checking your coin balance…");
  if (walletState !== "ready" || !wallets) return off("WALLET_UNAVAILABLE", "Your coin balance could not be loaded. Try again in a moment.");
  const have = wallets.coins.available;
  if (have < price) {
    const shortfall = price - have;
    return off("INSUFFICIENT_COINS", `Not enough coins: you need ${formatCoins(shortfall)} more (you have ${formatCoins(have)}).`, shortfall);
  }
  return { enabled: true, code: null, reason: null, shortfall: 0 };
}

// ---------------------------------------------------------------- idempotent submission

export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return `plot-${crypto.randomUUID()}`;
  return `plot-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

export type PurchaseOutcome =
  | { ok: true; response: PlotPurchaseResponse }
  | {
      ok: false;
      code: string;
      message: string;
      /** Safe to resend with the SAME key (network failure, 5xx, rate limit). */
      retryable: boolean;
      /** Listings/prices are stale: refresh and start a NEW attempt (new key, new body). */
      refresh: boolean;
    };

export type PostPurchase = (propertyId: string, body: PlotPurchaseRequest, idempotencyKey: string) => Promise<PlotPurchaseResponse>;

/**
 * One confirmation of one price for one plot. The Idempotency-Key is created exactly once, when the
 * visitor confirms, and every retry of that confirmation resends the same key and body, so the
 * server returns the original order instead of spending twice. A different plot or price is a
 * different attempt with a different key (the server rejects a reused key with another body).
 */
export class PurchaseAttempt {
  readonly key: string;
  readonly body: PlotPurchaseRequest;
  private tries = 0;
  private inFlight: Promise<PurchaseOutcome> | null = null;
  private done: PurchaseOutcome | null = null;

  constructor(
    readonly propertyId: string,
    expectedPriceCoins: number,
    makeKey: () => string = newIdempotencyKey,
  ) {
    this.key = makeKey();
    this.body = { unit: "COIN", expectedPriceCoins };
  }

  get attempts(): number {
    return this.tries;
  }

  get settled(): PurchaseOutcome | null {
    return this.done;
  }

  /** Send (or resend) this attempt. Concurrent calls share one request; a success is final. */
  submit(post: PostPurchase): Promise<PurchaseOutcome> {
    if (this.done?.ok) return Promise.resolve(this.done);
    if (this.inFlight) return this.inFlight;
    this.tries++;
    const run = (async (): Promise<PurchaseOutcome> => {
      try {
        const response = await post(this.propertyId, this.body, this.key);
        return { ok: true, response };
      } catch (err) {
        return describePurchaseError(err);
      }
    })();
    this.inFlight = run;
    return run.then((outcome) => {
      this.inFlight = null;
      this.done = outcome;
      return outcome;
    });
  }
}

/**
 * Keeps an UNRESOLVED attempt (e.g. the response was lost) for the same plot and price, so closing
 * and reopening the confirmation still resends the original key. Resolved attempts (success, or
 * a definitive refusal) are forgotten; a new price is always a new attempt.
 */
export class PurchaseAttemptRegistry {
  private readonly open = new Map<string, PurchaseAttempt>();

  constructor(private readonly makeKey: () => string = newIdempotencyKey) {}

  forListing(propertyId: string, expectedPriceCoins: number): PurchaseAttempt {
    const id = `${propertyId}:${expectedPriceCoins}`;
    let attempt = this.open.get(id);
    if (!attempt) {
      attempt = new PurchaseAttempt(propertyId, expectedPriceCoins, this.makeKey);
      this.open.set(id, attempt);
    }
    return attempt;
  }

  /** Forget an attempt that can no longer be retried with its key. */
  settle(attempt: PurchaseAttempt): void {
    const id = `${attempt.propertyId}:${attempt.body.expectedPriceCoins}`;
    if (this.open.get(id) === attempt) this.open.delete(id);
  }

  get size(): number {
    return this.open.size;
  }
}

/** One registry per page load (attempts never outlive the tab; the server keeps the real record). */
export const purchaseAttempts = new PurchaseAttemptRegistry();

/** Map every error code the purchase endpoint can return (FEEDBACK_ROUND1_BRIEF) to clear text. */
export function describePurchaseError(err: unknown): Extract<PurchaseOutcome, { ok: false }> {
  if (!isApiError(err)) {
    return {
      ok: false,
      code: "NETWORK",
      message: "Couldn't reach Townsy to confirm the purchase. Try again: a repeated attempt can never charge twice.",
      retryable: true,
      refresh: false,
    };
  }
  const fail = (message: string, opts: { retryable?: boolean; refresh?: boolean } = {}) => ({
    ok: false as const,
    code: err.code,
    message,
    retryable: opts.retryable ?? false,
    refresh: opts.refresh ?? false,
  });
  switch (err.code) {
    case "UNAUTHENTICATED":
      return fail("Sign in to buy a plot. Your session may have expired.");
    case "EMAIL_NOT_VERIFIED":
      return fail("Verify your email address before buying a plot.");
    case "FEATURE_DISABLED":
      return fail("Plot sales are switched off right now.");
    case "PROPERTY_TYPE_NOT_ELIGIBLE":
      return fail("Only residential plots can be bought at the Real Estate office.");
    case "PROPERTY_NOT_AVAILABLE":
      return fail("Someone else bought this plot first. Your coins were not spent — pick another plot.", { refresh: true });
    case "PRICE_CHANGED":
      return fail("The price changed since you opened this. Nothing was spent — review the new price and confirm again.", { refresh: true });
    case "PLOT_LIMIT_REACHED":
      return fail("You already own a plot. Each account can own one plot.", { refresh: true });
    case "INSUFFICIENT_COINS":
      return fail("You don't have enough coins for this plot. Nothing was spent.", { refresh: true });
    case "IDEMPOTENCY_CONFLICT":
      return fail("This confirmation clashed with an earlier request. Nothing extra was spent — please start the purchase again.", {
        refresh: true,
      });
    case "RATE_LIMITED":
      return fail("Too many attempts. Wait a moment, then try again.", { retryable: true });
    case "NOT_FOUND":
      return fail("This plot no longer exists.", { refresh: true });
    case "FORBIDDEN":
    case "BANNED":
      return fail("Your account can't buy plots right now.");
    case "VALIDATION_FAILED":
      return fail(`The purchase request was rejected: ${err.message || "invalid request"}.`, { refresh: true });
    case "DEPENDENCY_UNAVAILABLE":
    case "INTERNAL":
      return fail("Townsy couldn't confirm the purchase. Try again: a repeated attempt can never charge twice.", { retryable: true });
    default:
      return fail(err.message || "The purchase could not be completed.", { retryable: err.retryable });
  }
}
